if (!customElements.get('yns-review-marquee')) {
  customElements.define(
    'yns-review-marquee',
    class YnsReviewMarquee extends HTMLElement {
      constructor() {
        super();
        this.timeoutMs = 10000;
        this.maxPerRow = parseInt(this.dataset.perRow, 10) || 10;
        this.hydrated = false;
      }

      connectedCallback() {
        this.topRow = this.querySelector('.yns-review-marquee__row--top');
        this.bottomRow = this.querySelector('.yns-review-marquee__row--bottom');
        this.sourceUrl = this.dataset.sourceUrl;
        this.productHandle = this.dataset.productHandle || '';

        if (!this.topRow || !this.bottomRow || !this.sourceUrl) return;

        this.setAttribute('data-marquee-state', 'awaiting');

        // Fail-safe: hide section if fetch never resolves
        this.timeout = setTimeout(() => {
          if (!this.hydrated) {
            this.setAttribute('data-marquee-state', 'timed-out');
            this.setAttribute('hidden', '');
          }
        }, this.timeoutMs);

        fetch(this.sourceUrl, { credentials: 'omit' })
          .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
          .then((all) => this.hydrate(all))
          .catch(() => {
            this.setAttribute('data-marquee-state', 'fetch-failed');
            this.setAttribute('hidden', '');
          });
      }

      disconnectedCallback() {
        if (this.timeout) clearTimeout(this.timeout);
      }

      hydrate(all) {
        if (this.hydrated) return;
        if (!Array.isArray(all) || all.length === 0) {
          this.setAttribute('data-marquee-state', 'no-reviews');
          this.setAttribute('hidden', '');
          return;
        }

        // Filter to the current product; fall back to the full pool if nothing matches
        let pool = this.productHandle
          ? all.filter((r) => r && r.product === this.productHandle)
          : all.slice();
        if (pool.length === 0) pool = all.slice();

        // Require non-empty message text
        pool = pool.filter((r) => r && typeof r.message === 'string' && r.message.trim().length > 0);

        if (pool.length === 0) {
          this.setAttribute('data-marquee-state', 'no-reviews');
          this.setAttribute('hidden', '');
          return;
        }

        // Newest first
        pool.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

        // Cap to what the marquee can show across both rows
        const cap = this.maxPerRow * 2;
        const selected = pool.slice(0, cap);

        const cards = selected.map((r) => this.buildCard(r)).filter(Boolean);
        if (cards.length === 0) {
          this.setAttribute('data-marquee-state', 'no-reviews');
          this.setAttribute('hidden', '');
          return;
        }

        this.hydrated = true;
        if (this.timeout) clearTimeout(this.timeout);
        this.setAttribute('data-marquee-state', 'hydrated');

        const top = [];
        const bottom = [];
        cards.forEach((card, i) => {
          if (i % 2 === 0 && top.length < this.maxPerRow) top.push(card);
          else if (bottom.length < this.maxPerRow) bottom.push(card);
        });

        if (top.length < 3 && bottom.length > 3) {
          while (top.length < Math.min(3, bottom.length - 3) && bottom.length > 0) {
            top.push(bottom.shift());
          }
        }

        this.fillRow(this.topRow, top);
        this.fillRow(this.bottomRow, bottom);
        this.removeAttribute('hidden');
      }

      fillRow(row, cards) {
        if (!cards.length) {
          row.style.display = 'none';
          return;
        }
        // Duplicate cards for seamless loop with translateX(-50%) animation
        cards.forEach((card) => row.appendChild(card));
        cards.forEach((card) => row.appendChild(card.cloneNode(true)));
      }

      buildCard(review) {
        const body = (review.message || '').trim();
        if (!body) return null;

        const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 5)));
        const author = (review.reviewer || '').trim();

        const card = document.createElement('article');
        card.className = 'yns-review-marquee__card';

        const stars = document.createElement('div');
        stars.className = 'yns-review-marquee__stars';
        stars.setAttribute('aria-label', rating + ' out of 5 stars');
        stars.setAttribute('role', 'img');
        for (let i = 0; i < 5; i++) {
          const star = document.createElement('span');
          star.textContent = i < rating ? '★' : '☆';
          star.setAttribute('aria-hidden', 'true');
          stars.appendChild(star);
        }
        card.appendChild(stars);

        const quote = document.createElement('p');
        quote.className = 'yns-review-marquee__quote';
        quote.textContent = body;
        card.appendChild(quote);

        const footer = document.createElement('div');
        footer.className = 'yns-review-marquee__footer';

        const left = document.createElement('div');
        const authorEl = document.createElement('span');
        authorEl.className = 'yns-review-marquee__author';
        authorEl.textContent = author || 'Verified buyer';
        left.appendChild(authorEl);
        footer.appendChild(left);

        card.appendChild(footer);
        return card;
      }
    }
  );
}
